<template>
  <v-expansion-panels v-model="panel">
    <v-expansion-panel elevation="0" rounded="lg">
      <v-expansion-panel-title height="48px" style="background-color: #7077bb4D">
        <span class="reg-normal">{{ label }}</span>
        <template #actions="{ expanded }">
          <v-icon size="large" :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"/>
        </template>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-list v-if="logs && logs.length > 0" density="compact">
          <template v-for="(log, index) in logs">
            <v-list-item color="primary">
              <v-list-item-title>
                <span class="sm-normal text-primary">{{ log.message }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>
                <span class="tiny-normal">{{ log.creator + ' on ' + moment(log.timestamp, "DD-MM-YYYY HH:mm:ss").format("D MMMM YYYY HH:mm:ss") }}</span>
              </v-list-item-subtitle>
              <template v-if="canEdit && log.editable" #append>
                <v-list-item-action>
                  <v-menu>
                    <template #activator="{ props }">
                      <v-btn elevation="0" icon v-bind="props">
                        <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="editLog(index)">
                        <template #prepend><PhPencilSimpleLine size="24"/></template>
                        <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="deleteLog(index)">
                        <template #prepend><PhTrash size="24"/></template>
                        <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-action>
              </template>
            </v-list-item>
            <v-divider/>
          </template>
        </v-list>
        <div class="reg-normal text-primary mt-3" v-else>
          {{ $store.getters.translate("nothing_found") }}
        </div>
        <v-text-field
            v-if="!isDisabled"
            density="default"
            class="reg-normal text-primary mt-5"
            base-color="border-tertiary"
            bg-color="secondary"
            color="primary"
            v-model="new_message"
            @keyup.enter="insertContent()"
            hide-details
            single-line
            flat
            clearable/>
        <base-action-button v-if="!isDisabled" class="mt-5" @action="insertContent">
          <template #prepend><PhPlus style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("add") }}
        </base-action-button>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
  <edit-log ref="edit_log" @edited="logEdited" />
</template>

<script>
import helpFunctions from "../../plugins/helpFunctions";
import moment from "moment";
import BaseActionButton from "./BaseActionButton";
import EditLog from "../../views/projectmanagementlogs/Edit";
import {PhDotsThreeVertical, PhPencilSimpleLine, PhPlus, PhTrash} from "@phosphor-icons/vue";
import {useAbility} from "@casl/vue";

export default {
  components: {PhPencilSimpleLine, PhTrash, PhDotsThreeVertical, PhPlus, BaseActionButton, EditLog},
  props: ["category", "type", "model_id", "isDisabled", "label"],
  setup() {
    const { can } = new useAbility();
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.projectmanagementlogs,
      logs: [],
      new_message: null,
      panel: [],
      edited_message_index: null,
    };
  },
  mounted() {
    if(this.category !== 'Planner' && this.type !== 'checkbox') {
      this.panel = [0];
    }
    this.load();
  },
  methods: {
    async load() {
      if (this.model_id) {
        this.logs = [];
        let response = await helpFunctions.getCustomRoute(this.$toast, this.page.name + "?category=" + this.category + "&type=" + this.type + "&model_id=" + this.model_id);
        let temp_logs = response.split("\n");
        for (let i = 0; i < temp_logs.length; i++) {
          if(temp_logs[i] !== "") {
            let parts = temp_logs[i].split("] ", 2);
            this.logs.push({
              timestamp: parts[0].split(" - ")[0].replace("[", ""),
              creator: parts[0].split(" - ")[1],
              message: parts[1],
              editable: parts[0].split(" - ")[1] === this.$store.getters.getUser.person.casual_name,
              metadata: parts[0] + "] ",
            });
          }
        }
      }
    },
    async insertContent() {
      if (this.new_message) {
        let timestamp = moment().format("DD-MM-YYYY HH:mm:ss");
        let metadata = "[" + timestamp + " - " + this.$store.getters.getUser.person.casual_name + "] ";

        this.logs.unshift({
          timestamp: timestamp,
          creator: this.$store.getters.getUser.person.casual_name,
          message: this.new_message,
          editable: true,
          metadata: metadata
        });

        await helpFunctions.createModel(this.$toast, this.page.name, {
          category: this.category,
          type: this.type,
          project_management_id: this.model_id,
          new_message: metadata + this.new_message,
        });

        this.new_message = null;
      }
    },
    editLog(index) {
      this.edited_message_index = index;
      this.$refs.edit_log.load(this.logs[index].message);
    },
    async logEdited(new_message) {
      await helpFunctions.postCustomRoute(this.$toast, "edit-project-management-log", {
        category: this.category,
        type: this.type,
        project_management_id: this.model_id,
        old_message: this.logs[this.edited_message_index].metadata + this.logs[this.edited_message_index].message,
        new_message: this.logs[this.edited_message_index].metadata + new_message
      }, true, true);

      this.logs[this.edited_message_index].message = new_message;

      this.edited_message_index = null;
    },
    async deleteLog(index) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.postCustomRoute(this.$toast, "delete-project-management-log", {
            category: this.category,
            type: this.type,
            project_management_id: this.model_id,
            message_to_delete: this.logs[index].metadata + this.logs[index].message,
          }, true, true);

          this.logs.splice(index, 1);
        }
      });
    },
  },
  computed: {
    canEdit() {
      return this.can("edit", this.$createEntity("log", { id: 0 }));
    },
  },
  watch: {
    model_id: {
      handler() {
        this.load();
      },
    },
  },
};
</script>